import { create } from 'zustand';
import { useOrganizationStore } from '../store/organization.store';
import { collection, getDocs } from 'firebase/firestore';
import { getOrgCollectionPath } from '../constants/paths';

export interface CollectionData {
  id: string;
  displayName: string;
  [key: string]: any;
}

interface CollectionCache {
  // Global collections (shared across all orgs)
  agency: CollectionData[];
  bpm: CollectionData[];
  client: CollectionData[];
  genres: CollectionData[];
  keys: CollectionData[];
  tags: CollectionData[];
  territories: CollectionData[];
  flightingplatform: CollectionData[];
  // Org-scoped collections
  composers: CollectionData[];
  creativedirector: CollectionData[];
  producer: CollectionData[];
  sessionartists: CollectionData[];
  studio: CollectionData[];
  backuplocation: CollectionData[];
}

interface CollectionCacheState extends CollectionCache {
  isInitialized: boolean;
  error: string | null;
  currentOrgId: string | null; // Track which org the cache is for
  loadEssentialCollections: (firebase: any) => Promise<void>;
  loadCollectionData: (firebase: any, orgId?: string | null) => Promise<void>;
  loadOrgCollections: (firebase: any, orgId: string) => Promise<void>;
  clearCache: () => void;
  allTrackNames?: string[];
}

// Define collections based on role
// Global collections are shared across all organizations
const globalCollections = [
  'genres',
  'tags',
  'keys',
  'bpm',
  'agency',           // Global for cross-org normalization
  'client',           // Global for cross-org normalization
  'territories',      // Global for license territories
  'flightingplatform' // Global for cross-org normalization
];

// Organization-scoped collections (private to each org)
const orgScopedCollections = [
  'composers',
  'creativedirector',
  'producer',
  'sessionartists',
  'studio',
  'backuplocation'
];

export const useCollectionCache = create<CollectionCacheState>((set, get) => ({
  // Global collections
  agency: [],
  bpm: [],
  client: [],
  genres: [],
  keys: [],
  tags: [],
  territories: [],
  flightingplatform: [],
  // Org-scoped collections
  composers: [],
  creativedirector: [],
  producer: [],
  sessionartists: [],
  studio: [],
  backuplocation: [],
  // State
  isInitialized: false,
  error: null,
  currentOrgId: null,
  allTrackNames: [],

  loadEssentialCollections: async (firebase) => {
    // Pre-load all global collections (shared across all organizations)
    // These are: genres, tags, keys, bpm
    try {
      const collectionData: Partial<CollectionCache> = {};

      for (const collectionName of globalCollections) {
        try {
          const snapshot = await getDocs(collection(firebase.db, collectionName));
          const data = [] as CollectionData[];
          
          snapshot.docs.forEach((doc: any) => {
            const docData = doc.data();
            data.push({
              id: doc.id,
              displayName: docData.name || docData.label || docData.text || doc.id,
              ...docData
            });
          });

          collectionData[collectionName as keyof CollectionCache] = data;
        } catch (error) {
          throw error;
        }
      }

      // Update state with essential collections and mark as initialized for chatbot use
      set((state) => ({
        ...state,
        ...collectionData,
        isInitialized: true,
      }));
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Unknown error loading essential collections' });
    }
  },

  loadCollectionData: async (firebase, orgId?: string | null) => {
    // Load org-scoped collections for the specified organization
    // Global collections (genres, tags, keys, bpm) are loaded via loadEssentialCollections
    try {
      // Use organization-level role instead of global auth role
      const { currentOrgRole, isSuperAdmin } = useOrganizationStore.getState();
      const role = isSuperAdmin ? 'ADMIN' : (currentOrgRole || 'USER');
      // Get orgId from parameter or from organization store
      const currentOrgId = orgId ?? useOrganizationStore.getState().currentOrgId;

      const collectionData: Partial<CollectionCache> = {};

      // Load organization-scoped collections if user has ADMIN/OPERATOR role
      // and an organization is selected
      if (['ADMIN', 'OPERATOR'].includes(role) && currentOrgId) {
        for (const collectionName of orgScopedCollections) {
          try {
            // Get the org-scoped collection path
            const orgCollectionPath = getOrgCollectionPath(currentOrgId, collectionName);
            const snapshot = await getDocs(collection(firebase.db, orgCollectionPath));
            const data = [] as CollectionData[];
            snapshot.docs.forEach((doc: any) => {
              const docData = doc.data();
              data.push({
                id: doc.id,
                displayName: docData.name || docData.label || docData.text || doc.id,
                ...docData
              });
            });
            collectionData[collectionName as keyof CollectionCache] = data;
          } catch (error) {
            // If org collection doesn't exist yet, initialize with empty array
            collectionData[collectionName as keyof CollectionCache] = [];
          }
        }
      }

      // Fetch all track names from audiometa for the current org
      let allTrackNames: string[] = [];
      if (currentOrgId) {
        try {
          const audiometaPath = getOrgCollectionPath(currentOrgId, 'audiometa');
          const audiometaSnapshot = await getDocs(collection(firebase.db, audiometaPath));
          allTrackNames = audiometaSnapshot.docs
            .map((doc: any) => {
              const data = doc.data();
              return data.trackName || data.trackname || '';
            })
            .filter((name: string) => typeof name === 'string' && name.trim().length > 0);
        } catch (err) {
          // Error loading track names - continue with empty array
        }
      }

      set({
        ...collectionData as CollectionCache,
        isInitialized: true,
        error: null,
        currentOrgId: currentOrgId || null,
        allTrackNames,
      });

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
      set({ error: errorMessage });
    }
  },

  /**
   * Load only organization-scoped collections for a specific org
   * Used when switching organizations without reloading global data
   */
  loadOrgCollections: async (firebase, orgId: string) => {
    // Clear org-scoped collections before loading new ones
    // Note: Global collections (agency, client, territories, flightingplatform) are NOT cleared
    set({
      composers: [],
      creativedirector: [],
      producer: [],
      sessionartists: [],
      studio: [],
      backuplocation: [],
      allTrackNames: [],
    });

    try {
      // Use organization-level role instead of global auth role
      const { currentOrgRole, isSuperAdmin } = useOrganizationStore.getState();
      const role = isSuperAdmin ? 'ADMIN' : (currentOrgRole || 'USER');
      if (!['ADMIN', 'OPERATOR'].includes(role)) {
        set({ currentOrgId: orgId });
        return;
      }

      const collectionData: Partial<CollectionCache> = {};

      for (const collectionName of orgScopedCollections) {
        try {
          const orgCollectionPath = getOrgCollectionPath(orgId, collectionName);
          const snapshot = await getDocs(collection(firebase.db, orgCollectionPath));
          const data = [] as CollectionData[];
          snapshot.docs.forEach((doc: any) => {
            const docData = doc.data();
            data.push({
              id: doc.id,
              displayName: docData.name || docData.label || docData.text || doc.id,
              ...docData
            });
          });
          collectionData[collectionName as keyof CollectionCache] = data;
        } catch (error) {
          collectionData[collectionName as keyof CollectionCache] = [];
        }
      }

      // Fetch track names for this org
      let allTrackNames: string[] = [];
      try {
        const audiometaPath = getOrgCollectionPath(orgId, 'audiometa');
        const audiometaSnapshot = await getDocs(collection(firebase.db, audiometaPath));
        allTrackNames = audiometaSnapshot.docs
          .map((doc: any) => {
            const data = doc.data();
            return data.trackName || data.trackname || '';
          })
          .filter((name: string) => typeof name === 'string' && name.trim().length > 0);
      } catch (err) {
        // Error loading track names - continue with empty array
      }

      set((state) => ({
        ...state,
        ...collectionData,
        currentOrgId: orgId,
        allTrackNames,
      }));

    } catch (error) {
      // Failed to load org collections
    }
  },

  clearCache: () => {
    set({
      // Global collections
      agency: [],
      bpm: [],
      client: [],
      genres: [],
      keys: [],
      tags: [],
      territories: [],
      flightingplatform: [],
      // Org-scoped collections
      composers: [],
      creativedirector: [],
      producer: [],
      sessionartists: [],
      studio: [],
      backuplocation: [],
      // State
      isInitialized: false,
      error: null,
      currentOrgId: null,
      allTrackNames: [],
    });
  }
})); 